import { orderOverviewId } from '@/constants/order.constants';
import {
  ValidOrderEditStatusesForDealer,
  ValidOrderEditStatusesForSalesRep,
} from '@/constants/orderEditValidOrderStatuses';
import {
  DraftModel,
  LegalEntityEnum,
  OrderDocumentStatus,
  OrderModel,
  OrderStatus,
  OrderType,
  SalesInvoiceHeaderModel,
  UserModel,
} from '@onrunning/backstage-models';
import { DocumentType } from '../models/document-type.enum';
import { MatrixType } from '../models/matrix-type.enum';
import { OrderDocumentType } from '../models/order-document-type.enum';
import { OrderDocument } from '../models/order-document.model';
import { OrderHeaderModel } from '../models/order-header.model';
import type { Order } from '../models/order.model';
import { SalesInvoiceHeaderModel as LocalSalesInvoiceHeaderModel } from '../models/sales-invoice-header.model';
import { formatMixins } from './format.mixins';
import { localeMixins } from './locale.mixins';

const isOrderEditByDealerFeatureEnabled = import.meta.env.VITE_APP_isOrderEditByDealerFeatureEnabled === 'true';

export const ordersMixins = {
  hasDocuments(order: OrderHeaderModel | Order, t: any, locale: string) {
    const documents = this.getOrderHeaderDocuments(order, t, locale);

    return documents.length > 0;
  },
  getOrderHeaderDocuments(order: OrderHeaderModel | Order, t: any, locale: string) {
    const documents = new Array<OrderDocument>();

    if (order.orderConfirmation) {
      order.orderConfirmation.orderDocumentType = OrderDocumentType.OrderConfirmation;
      order.orderConfirmation.description = 'viewOrders.orders_list_columns.order_confirmation';
      order.orderConfirmation.displayName =
        order.orderConfirmation.displayName || t('viewOrders.orders_list_columns.order_confirmation');
      documents.push(order.orderConfirmation);
    }

    if (order.packingSlips && order?.packingSlips?.length > 0) {
      documents.push(
        ...order.packingSlips.map((orderDocument: OrderDocument) => {
          const document = Object.assign(new OrderDocument(), orderDocument);

          if (document.name) {
            document.displayName = formatMixins.formatDate(new Date(document.name), locale);
          }

          document.orderDocumentType = OrderDocumentType.PackingSlip;
          document.description = 'viewOrders.orders_list_columns.packing_slip';
          document.id = orderDocument.id;

          return document;
        }),
      );
    }

    if (order.salesInvoiceHeaders && order.salesInvoiceHeaders?.length > 0) {
      documents.push(
        ...order.salesInvoiceHeaders.map((salesInvoice: SalesInvoiceHeaderModel | LocalSalesInvoiceHeaderModel) => {
          const document = new OrderDocument();

          document.orderNumber = salesInvoice.salesOrderNumber;
          document.packingSlipDate = salesInvoice.invoiceDate.toString();
          document.name = salesInvoice.invoiceNumber;
          document.deliveryNumber = salesInvoice.invoiceNumber;
          document.id = salesInvoice.invoiceNumber;
          document.description = 'viewOrders.orders_list_columns.invoice';

          if (document.deliveryNumber?.startsWith('SCR')) {
            document.description = 'viewOrders.orders_list_columns.credit_note';
          }

          document.orderDocumentType = OrderDocumentType.Invoice;

          return document;
        }),
      );
    }

    return documents;
  },
  getDocumentType(orderDocumentType?: OrderDocumentType): DocumentType | undefined {
    switch (orderDocumentType) {
      case OrderDocumentType.OrderConfirmation:
        return DocumentType.OrderConfirmation;
      case OrderDocumentType.Invoice:
        return DocumentType.Invoice;
      case OrderDocumentType.PackingSlip:
        return DocumentType.DeliveryNote;
      case OrderDocumentType.CreditNote:
        return DocumentType.CreditNote;
    }
  },
  getDocumentTypeByDocumentNumberForPaymentDocuments(documentNumber: string): DocumentType {
    if (documentNumber.startsWith('SCR')) {
      return DocumentType.CreditNote;
    }

    return DocumentType.Invoice;
  },
  getOrderDocumentDownloadButtonText(document: OrderDocument, t: any, locale: string): string {
    const result = [];
    const separator = ' — ';

    if (document.description) {
      result.push(t(document.description));
    }

    if (document.deliveryNumber) {
      result.push(document.deliveryNumber);
    }
    2;
    if (document.packingSlipDate) {
      result.push(formatMixins.formatDate(document.packingSlipDate, locale));
    }

    return result.join(separator);
  },
  getOrderType(matrtixType: MatrixType): OrderType {
    switch (matrtixType) {
      case MatrixType.order:
        return OrderType.ReOrder;
      case MatrixType.preorder:
        return OrderType.PreOrder;
      case MatrixType.return:
        return OrderType.ReturnOrder;
      case MatrixType.warranty:
        return OrderType.WarrantyOrder;
      case MatrixType.swap:
        return OrderType.WarrantyOrder;
      case MatrixType.availability:
        return OrderType.ReOrder;
    }
  },
  // ToDo ideally we would share this on the shared backstage/mappers lib
  mapOrderTypeByOrderType(orderType?: string): OrderType {
    switch (orderType?.toLocaleLowerCase()) {
      case 'bulks':
        return OrderType.Bulks;
      case 'consignment return':
        return OrderType.ConsignmentReturn;
      case 'consignment sale':
        return OrderType.ConsignmentSale;
      case 'demo sets':
        return OrderType.DemoSets;
      case 'drop ship':
        return OrderType.DropShip;
      case 'event order':
        return OrderType.EventOrder;
      case 'experience items':
        return OrderType.ExperienceItems;
      case 'marketing material':
        return OrderType.MarketingMaterial;
      case 'media samples':
        return OrderType.MediaSamples;
      case 'pre-order':
        return OrderType.PreOrder;
      case 'pre-order-multiple':
        return OrderType.MultiplePreOrder;
      case 're-order':
        return OrderType.ReOrder;
      case 'replacement':
        return OrderType.Replacement;
      case 'return':
        return OrderType.ReturnOrder;
      case 'sales team samples':
        return OrderType.SalesTeamSamples;
      case 'sponsorship':
        return OrderType.Sponsorship;
      case 'stock clearance':
        return OrderType.StockClearance;
      case 'subscription':
        return OrderType.Subscription;
      case 'warranty':
        return OrderType.WarrantyOrder;
      default:
        return OrderType.Unknown;
    }
  },
  displayOrderType(
    params: any,
    orderTypeFieldName = 'type',
    legalEntity: LegalEntityEnum,
    t: any /* ToDo try to improve type */,
  ) {
    if (params.data[orderTypeFieldName]) {
      const title = localeMixins.getLocaleTextByOrderType(
        'viewOrders.order_type.',
        params.data[orderTypeFieldName],
        legalEntity as LegalEntityEnum,
      );

      return t(title).toString();
    }

    return '';
  },
  isOrderInUpdateProgress(order: OrderModel): boolean {
    return order.status === OrderStatus.UpdateInProgress;
  },
  theOrderIsDraft(order: DraftModel | OrderModel) {
    return order.status === OrderStatus.NotSubmitted;
  },
  isOrderEditableForDealer(order: Order, currentUser: UserModel | undefined | null): boolean {
    if (!order?.userId || !currentUser?.id || this.isOverviewPage(order)) {
      return false;
    }

    const isCurrentUserTheCreator = order.userId === currentUser.id;

    return (
      isCurrentUserTheCreator &&
      isOrderEditByDealerFeatureEnabled &&
      order.isEditedByDealer === false &&
      ValidOrderEditStatusesForDealer.includes(order.status.toLocaleLowerCase() as OrderStatus)
    );
  },
  isOrderEditableForSalesReps(order: Order): boolean {
    if (!order || this.isOverviewPage(order)) {
      return false;
    }

    return (
      ValidOrderEditStatusesForSalesRep.includes(order.status.toLocaleLowerCase() as OrderStatus) &&
      order.documentStatus === OrderDocumentStatus.Confirmation
    );
  },
  isOverviewPage(order: Order): boolean {
    return order.orderNumber === orderOverviewId;
  },
  isPreOrderVariant(orderType: OrderType | string | undefined): boolean {
    return orderType
      ? [OrderType.PreOrder.toLocaleLowerCase(), OrderType.MultiplePreOrder.toLocaleLowerCase()].includes(
          orderType.toLocaleLowerCase(),
        )
      : false;
  },
};
